import { contrastingText, legibleOn, oklabMix, srgbMix, isParseable, relativeLuminance, } from "./color.js";
import { FIELDS } from "./fields.js";
import { migrateBrandConfig } from "./migrate.js";
import { nestBrandConfig } from "./shape.js";
export { parseColor, rgbToHex, oklabMix, srgbMix, relativeLuminance, contrastRatio, meetsContrast, contrastingText, legibleOn, WCAG_MIN_RATIO, } from "./color.js";
export { toDesignTokens } from "./design-tokens.js";
/**
 * The colour pairs the Contrast panel checks, as field keys. `auto` marks a pair
 * whose foreground the engine corrects for legibility, so the panel can say the
 * pass was earned rather than authored.
 */
export const CONTRAST_PAIRS = [
    { label: "Button text on primary button", fg: "primaryButtonText", bg: "primaryButton", threshold: "AA-normal", auto: true },
    { label: "Secondary button text on its fill", fg: "secondaryButtonText", bg: "secondaryButton", threshold: "AA-normal", auto: true },
    { label: "Body text on page", fg: "text", bg: "pageBackground", threshold: "AA-normal", auto: true },
    { label: "Body text on cards", fg: "text", bg: "cardBackground", threshold: "AA-normal" },
    { label: "Accent on page", fg: "secondary", bg: "pageBackground", threshold: "AA-large" },
    { label: "Accent on cards", fg: "secondary", bg: "cardBackground", threshold: "AA-large" },
    { label: "Text button on cards", fg: "textButton", bg: "cardBackground", threshold: "AA-normal", auto: true },
    { label: "Icons on cards", fg: "icon", bg: "cardBackground", threshold: "AA-large" },
    { label: "Muted text on cards", fg: "textMuted", bg: "cardBackground", threshold: "AA-large" },
    { label: "Field text on field fill", fg: "text", bg: "fieldBackground", threshold: "AA-normal" },
    { label: "Menu text on menu fill", fg: "text", bg: "menuBackground", threshold: "AA-normal" },
    { label: "Link on page", fg: "link", bg: "pageBackground", threshold: "AA-normal", auto: true },
    { label: "Navbar text on navbar", fg: "navbarText", bg: "navbarBackground", bgImage: "navbarBackgroundImage", threshold: "AA-normal" },
    { label: "Footer text on footer", fg: "footerText", bg: "footerBackground", bgImage: "footerBackgroundImage", threshold: "AA-normal" },
];
const LIGHT_DEFAULT = Object.fromEntries(FIELDS.map((f) => [f.key, f.default]));
const DARK_DEFAULT = Object.fromEntries(FIELDS
    .filter((f) => f.darkValue)
    .map((f) => [f.key, f.darkValue]));
const HOVER = 0.1;
const ACTIVE = 0.18;
const PRESSED_RATIO = ACTIVE / HOVER;
const LIGHT_FILL_LUMINANCE = 0.35;
const TINT = 0.08;
const STATUS_FILL = 0.1;
const STATUS = ["error", "success", "warning", "info"];
function pick(scope, ...keys) {
    for (const k of keys)
        if (scope[k] != null)
            return scope[k];
    return undefined;
}
function percentWeight(value) {
    if (value == null)
        return undefined;
    const m = /^(\d+|\d*\.\d+)%$/.exec(value.trim());
    if (!m)
        return undefined;
    return Math.min(1, Math.max(0, parseFloat(m[1]) / 100));
}
function buttonStates(fill, light, dark) {
    const luminance = relativeLuminance(fill);
    if (luminance == null)
        return undefined;
    const isLightFill = luminance >= LIGHT_FILL_LUMINANCE;
    const overlay = isLightFill ? "#000000" : "#ffffff";
    const weight = isLightFill ? light : dark;
    return {
        hover: srgbMix(fill, overlay, weight),
        pressed: srgbMix(fill, overlay, Math.min(1, weight * PRESSED_RATIO)),
    };
}
function divideLength(value, by) {
    const m = /^(\d+|\d*\.\d+)(px|rem|em)$/.exec(value.trim());
    if (!m)
        return value.trim() === "0" ? "0" : undefined;
    const n = parseFloat(m[1]) / by;
    return `${parseFloat(n.toFixed(4))}${m[2]}`;
}
function derive(scope, def, hues = scope) {
    const out = {};
    const set = (key, value) => {
        if (value != null && isParseable(value))
            out[key] = value;
    };
    const page = scope.pageBackground;
    const card = scope.cardBackground ?? def.cardBackground ?? LIGHT_DEFAULT.cardBackground;
    if (page != null && isParseable(page)) {
        const text = scope.text ?? contrastingText(page);
        set("text", scope.text ?? contrastingText(page));
        set("textMuted", scope.textMuted ?? oklabMix(text, page, 0.45));
    }
    const mainButton = pick(hues, "primaryButton", "primary");
    const secondaryButton = pick(hues, "secondaryButton", "secondary");
    if (mainButton != null)
        set("primaryButtonText", contrastingText(mainButton));
    if (secondaryButton != null)
        set("secondaryButtonText", contrastingText(secondaryButton));
    const textButton = pick(hues, "textButton", "primary");
    if (textButton != null)
        set("textButton", legibleOn(textButton, card));
    const link = pick(hues, "link", "textButton", "primary");
    if (link != null && page != null && isParseable(page))
        set("link", legibleOn(link, page));
    const hoverLight = percentWeight(scope.buttonHoverOnLightFill ?? LIGHT_DEFAULT.buttonHoverOnLightFill) ??
        HOVER;
    const hoverDark = percentWeight(scope.buttonHoverOnDarkFill ?? LIGHT_DEFAULT.buttonHoverOnDarkFill) ??
        HOVER;
    const main = mainButton && buttonStates(mainButton, hoverLight, hoverDark);
    if (main) {
        set("primaryButtonHover", main.hover);
        set("primaryButtonPressed", main.pressed);
    }
    const second = secondaryButton && buttonStates(secondaryButton, hoverLight, hoverDark);
    if (second) {
        set("secondaryButtonHover", second.hover);
        set("secondaryButtonPressed", second.pressed);
    }
    const tintBase = page ?? def.pageBackground ?? LIGHT_DEFAULT.pageBackground;
    if (hues.primary != null)
        set("primaryTint", oklabMix(hues.primary, tintBase, TINT));
    if (hues.secondary != null)
        set("secondaryTint", oklabMix(hues.secondary, tintBase, TINT));
    for (const family of STATUS) {
        if (scope[family] != null)
            set(`${family}Background`, oklabMix(scope[family], card, STATUS_FILL));
    }
    return out;
}
export function autofillBrand(config, { onlyMissing = true } = {}) {
    const base = config;
    const merge = (target, fills) => {
        for (const [k, v] of Object.entries(fills))
            if (!onlyMissing || target[k] == null)
                target[k] = v;
    };
    const result = { ...config };
    const fills = derive(base, LIGHT_DEFAULT);
    if (base.radius != null) {
        const suggested = divideLength(base.radius, 3);
        if (suggested != null)
            fills.fieldRadius = suggested;
    }
    merge(result, fills);
    if (config.dark && typeof config.dark === "object") {
        const authoredDark = config.dark;
        const darkScope = { ...base, ...DARK_DEFAULT, ...authoredDark };
        const darkFills = derive(darkScope, DARK_DEFAULT, {
            ...base,
            ...authoredDark,
        });
        const nextDark = { ...authoredDark };
        for (const [k, v] of Object.entries(darkFills))
            if (!onlyMissing || nextDark[k] == null)
                nextDark[k] = v;
        result.dark = nextDark;
    }
    return result;
}
// An authored brand holds only what a person chose; the derived values are
// filled in once, here, so no source file can carry a stale one.
export function publishBrand(authored) {
    return autofillBrand(migrateBrandConfig(authored), {
        onlyMissing: true,
    });
}
export function publishBrandJson(authored) {
    return nestBrandConfig(publishBrand(authored));
}
